import { Controller } from "@hotwired/stimulus";
import Cropper from "cropperjs";

export class CropperController extends Controller {
  static targets = [
    "input",
    "container",
    "label",
    "background",
    "buttonDisabled",
    "errorMessage",
    "form",
  ];

  static values = {
    photoType: { type: String, default: "profile" },
  };

  connect() {
    this.cropper = null;
    this.image = null;
    this.objectUrl = null;
  }

  get cropConfig() {
    if (this.photoTypeSafe === "background") {
      return {
        aspectRatio: 16 / 9,
        width: 1280,
        height: 720,
      };
    }

    return {
      aspectRatio: 1,
      width: 300,
      height: 300,
    };
  }

  get photoTypeSafe() {
    const allowed = ["profile", "background"];
    return allowed.includes(this.photoTypeValue)
      ? this.photoTypeValue
      : "profile";
  }

  loadImage(event) {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      this.cancel();
      this.errorMessageTarget.style.display = "block";
      return;
    }

    this.errorMessageTarget.style.display = "none";

    this.cleanup();

    const config = this.cropConfig;

    this.objectUrl = URL.createObjectURL(file);

    this.image = document.createElement("img");
    this.image.src = this.objectUrl;
    this.image.style.maxWidth = "100%";

    this.containerTarget.innerHTML = "";
    this.containerTarget.appendChild(this.image);

    this.cropper = new Cropper(this.image, {
      aspectRatio: config.aspectRatio,
      viewMode: 1,
      autoCropArea: 1,
      background: true,
      movable: true,
      zoomable: true,
      rotatable: true,
      scalable: true,
      responsive: true,
      minCropBoxWidth: 300,
      minCropBoxHeight: 300,
      ready: () => {
        this.labelTarget.style.display = "none";
        this.backgroundTarget.classList.add("cropper__view--background");
        this.buttonDisabledTarget.removeAttribute("disabled");
      },
    });
  }

  // Actions
  zoomIn() {
    this.cropper?.zoom(0.1);
  }

  zoomOut() {
    this.cropper?.zoom(-0.1);
  }

  rotateLeft() {
    this.cropper?.rotate(-45);
  }

  rotateRight() {
    this.cropper?.rotate(45);
  }

  flipX() {
    if (!this.cropper) return;
    const scaleX = this.cropper.getData().scaleX || 1;
    this.cropper.scaleX(-scaleX);
  }

  flipY() {
    if (!this.cropper) return;
    const scaleY = this.cropper.getData().scaleY || 1;
    this.cropper.scaleY(-scaleY);
  }

  reset() {
    this.cropper?.reset();
  }

  cancel() {
    this.cleanup();

    this.labelTarget.style.display = "flex";
    this.backgroundTarget.classList.remove("cropper__view--background");
    this.containerTarget.innerHTML = "";
    this.inputTarget.value = "";

    this.buttonDisabledTarget.setAttribute("disabled", "true");
    this.errorMessageTarget.style.display = "none";
  }

  submitForm(event) {
    event.preventDefault();

    if (!this.cropper) return;

    const config = this.cropConfig;

    this.buttonDisabledTarget.setAttribute("disabled", "true");

    const canvas = this.cropper.getCroppedCanvas({
      width: config.width,
      height: config.height,
    });

    canvas.toBlob(
      (blob) => {
        const file = new File([blob], "image.png", {
          type: "image/png",
        });

        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);

        this.inputTarget.files = dataTransfer.files;

        this.formTarget.submit();
      },
      "image/png",
      0.6,
    );
  }

  cleanup() {
    if (this.cropper) {
      this.cropper.destroy();
      this.cropper = null;
    }

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
